import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import { useAuth } from "./auth";
import { AppShell, LoadingView } from "./components";
import { isDomainShowcaseHost } from "./showcase-host";
import { MARKETING_SOLUTION_PATHS } from "../shared/marketing-seo";
import { LANDING_CONTENT } from "./marketing/landings-content";

const DashboardPage = lazy(() => import("./pages/DashboardPage").then((module) => ({ default: module.DashboardPage })));
const LoginPage = lazy(() => import("./pages/LoginPage").then((module) => ({ default: module.LoginPage })));
const SignupPage = lazy(() => import("./pages/SignupPage").then((module) => ({ default: module.SignupPage })));
const DocumentsAlertsPage = lazy(() => import("./pages/ModulePages").then((module) => ({ default: module.DocumentsAlertsPage })));
const MorePage = lazy(() => import("./pages/ModulePages").then((module) => ({ default: module.MorePage })));
const NotFoundPage = lazy(() => import("./pages/ModulePages").then((module) => ({ default: module.NotFoundPage })));
const CommercialPage = lazy(() => import("./pages/CommercialPage").then((module) => ({ default: module.CommercialPage })));
const SaleDetailPage = lazy(() => import("./pages/SaleDetailPage").then((module) => ({ default: module.SaleDetailPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then((module) => ({ default: module.SettingsPage })));
const StockPage = lazy(() => import("./pages/StockPage").then((module) => ({ default: module.StockPage })));
const VehicleDetailPage = lazy(() => import("./pages/VehicleDetailPage").then((module) => ({ default: module.VehicleDetailPage })));
const PublicShowcasePage = lazy(() => import("./pages/PublicShowcasePage").then((module) => ({ default: module.PublicShowcasePage })));
const PublicVehiclePage = lazy(() => import("./pages/PublicShowcasePage").then((module) => ({ default: module.PublicVehiclePage })));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage").then((module) => ({ default: module.AnalyticsPage })));
const MarketingLayout = lazy(() => import("./marketing/MarketingLayout").then((module) => ({ default: module.MarketingLayout })));
const MarketingHomePage = lazy(() => import("./marketing/HomePage").then((module) => ({ default: module.HomePage })));
const MarketingFeaturesPage = lazy(() => import("./marketing/FeaturesPage").then((module) => ({ default: module.FeaturesPage })));
const MarketingPricingPage = lazy(() => import("./marketing/PricingPage").then((module) => ({ default: module.PricingPage })));
const MarketingFaqPage = lazy(() => import("./marketing/FaqPage").then((module) => ({ default: module.FaqPage })));
const MarketingContactPage = lazy(() => import("./marketing/ContactPage").then((module) => ({ default: module.ContactPage })));
const MarketingLandingPage = lazy(() => import("./marketing/LandingPage").then((module) => ({ default: module.LandingPage })));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage").then((module) => ({ default: module.NotificationsPage })));
const IdentityAccessPage = lazy(() => import("./pages/IdentityAccessPages").then((module) => ({ default: module.IdentityAccessPage })));
const LegalPage = lazy(() => import("./pages/LegalPage").then((module) => ({ default: module.LegalPage })));
const WorkshopPage = lazy(() => import("./pages/WorkshopPage").then((module) => ({ default: module.WorkshopPage })));
const SuppliersPage = lazy(() => import("./pages/SuppliersPage"));
const AppraisalsPage = lazy(() => import("./pages/AppraisalsPage"));
const PublicationsPage = lazy(() => import("./pages/PublicationsPage"));
const SumautoPage = lazy(() => import("./pages/SumautoPage"));
const AgendaPage = lazy(() => import("./pages/AgendaPage").then((module) => ({ default: module.AgendaPage })));
const AccountPage = lazy(() => import("./pages/AccountPage"));

function ProtectedApp() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingView label="Preparando Cochegest…" />;
  if (!user) return <Navigate to={`/login?from=${encodeURIComponent(location.pathname + location.search + location.hash)}`} replace />;
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

export default function App() {
  const domainShowcase = typeof window !== "undefined" && isDomainShowcaseHost(window.location.hostname);
  return (
    <Suspense fallback={<LoadingView label="Preparando Cochegest…" />}>
      {domainShowcase ? <Routes>
        <Route path="/" element={<PublicShowcasePage hostMode />} />
        <Route path="/vehicles/:vehicleId" element={<PublicVehiclePage hostMode />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes> : <Routes>
        <Route element={<MarketingLayout />}>
          <Route path="/" element={<MarketingHomePage />} />
          <Route path="/funcionalidades" element={<MarketingFeaturesPage />} />
          <Route path="/precios" element={<MarketingPricingPage />} />
          <Route path="/faq" element={<MarketingFaqPage />} />
          <Route path="/contacto" element={<MarketingContactPage />} />
          {MARKETING_SOLUTION_PATHS.map((path) => {
            const content = LANDING_CONTENT[path];
            return content ? <Route key={path} path={path} element={<MarketingLandingPage content={content} />} /> : null;
          })}
        </Route>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/registro" element={<SignupPage />} />
        <Route path="/forgot-password" element={<IdentityAccessPage mode="forgot" />} />
        <Route path="/reset-password" element={<IdentityAccessPage mode="reset" />} />
        <Route path="/accept-invitation" element={<IdentityAccessPage mode="invitation" />} />
        <Route path="/legal/:document" element={<LegalPage />} />
        <Route path="/s/:slug" element={<PublicShowcasePage />} />
        <Route path="/s/:slug/vehicles/:vehicleId" element={<PublicVehiclePage />} />
        <Route path="/app" element={<ProtectedApp />}>
          <Route index element={<DashboardPage />} />
          <Route path="agenda" element={<AgendaPage />} />
          <Route path="cuenta" element={<AccountPage />} />
          <Route path="stock" element={<StockPage />} />
          <Route path="stock/:vehicleId" element={<VehicleDetailPage />} />
          <Route path="leads" element={<CommercialPage />} />
          <Route path="ventas/:saleId" element={<SaleDetailPage />} />
          <Route path="configuracion" element={<SettingsPage />} />
          <Route path="settings/billing" element={<SettingsPage />} />
          <Route path="inteligencia" element={<AnalyticsPage />} />
          <Route path="documentos" element={<DocumentsAlertsPage />} />
          <Route path="taller" element={<WorkshopPage />} />
          <Route path="proveedores" element={<SuppliersPage />} />
          <Route path="tasaciones" element={<AppraisalsPage />} />
          <Route path="anuncios" element={<PublicationsPage />} />
          <Route path="anuncios/sumauto" element={<SumautoPage />} />
          <Route path="alertas" element={<NotificationsPage />} />
          <Route path="mas" element={<MorePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>}
    </Suspense>
  );
}
